import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Loader2, Sparkles, XCircle, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, PageHeader, Button, Badge } from '../components/ui';
import { businessApi, type AIRecommendation, type Business } from '../lib/api';

const priorityTones: Record<string, 'danger' | 'warning' | 'neutral'> = {
  high: 'danger',
  medium: 'warning',
  low: 'neutral',
};

const priorityLabels: Record<string, string> = {
  high: 'Высокий',
  medium: 'Средний',
  low: 'Низкий',
};

const categoryLabels: Record<string, string> = {
  reviews: 'Отзывы',
  seo: 'SEO',
  content: 'Контент',
  ads: 'Реклама',
};

const statusWeight: Record<string, number> = {
  new: 0,
  done: 1,
  dismissed: 2,
};

export function ClientOptimization() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [business, setBusiness] = useState<Business | null>(null);
  const [recommendations, setRecommendations] = useState<AIRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const loadData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const [businessRes, recommendationsRes] = await Promise.all([
        businessApi.getById(id),
        businessApi.getRecommendations(id),
      ]);
      setBusiness(businessRes.data.data);
      setRecommendations(recommendationsRes.data.data);
    } catch (e) {
      console.error(e);
      setError('Не удалось загрузить раздел оптимизации');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const sortedRecommendations = useMemo(
    () =>
      [...recommendations].sort(
        (a, b) => (statusWeight[a.status] ?? 99) - (statusWeight[b.status] ?? 99),
      ),
    [recommendations],
  );

  const handleGenerate = async () => {
    if (!id) return;
    try {
      setGenerating(true);
      await businessApi.generateRecommendations(id);
      await loadData();
      toast.success('Рекомендации обновлены');
    } catch (e) {
      console.error(e);
      toast.error('Не удалось сгенерировать рекомендации');
    } finally {
      setGenerating(false);
    }
  };

  const handleRecAction = async (recId: string, status: 'done' | 'dismissed') => {
    try {
      setUpdatingId(recId);
      await businessApi.updateRecommendation(recId, status);
      setRecommendations((prev) =>
        prev.map((item) => (item.id === recId ? { ...item, status } : item)),
      );
    } catch (e) {
      console.error(e);
      toast.error('Не удалось обновить статус рекомендации');
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="p-6">
        <Card>
          <p className="text-red-600 text-sm">{error || 'Клиент не найден'}</p>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={`Оптимизация: ${business.name}`}
        subtitle="Все рекомендации по улучшению карточки и продвижения"
      />

      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/client/${business.id}/dashboard`)}
            icon={<ArrowLeft className="w-4 h-4" />}
          >
            Вернуться в дашборд клиента
          </Button>

          <Button
            type="button"
            variant="primary"
            onClick={handleGenerate}
            loading={generating}
            disabled={generating}
            icon={generating ? undefined : <Sparkles className="w-4 h-4" />}
          >
            {generating ? 'Генерация...' : 'Обновить рекомендации'}
          </Button>
        </div>

        {sortedRecommendations.length === 0 ? (
          <Card>
            <div className="py-8 text-center text-gray-500 text-sm">
              Рекомендации пока не сгенерированы
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {sortedRecommendations.map((rec) => (
              <Card key={rec.id}>
                <div className="flex items-start gap-3">
                  <Zap
                    className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      rec.priority === 'high'
                        ? 'text-red-500'
                        : rec.priority === 'medium'
                          ? 'text-yellow-500'
                          : 'text-gray-400'
                    }`}
                  />

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-900">{rec.title}</span>
                      <Badge tone={priorityTones[rec.priority] || 'neutral'}>
                        {priorityLabels[rec.priority] || rec.priority}
                      </Badge>
                      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-primary-50 text-primary-700">
                        {categoryLabels[rec.category] || rec.category}
                      </span>
                      <Badge
                        tone={
                          rec.status === 'done'
                            ? 'success'
                            : rec.status === 'dismissed'
                              ? 'neutral'
                              : 'warning'
                        }
                      >
                        {rec.status === 'done'
                          ? 'Выполнено'
                          : rec.status === 'dismissed'
                            ? 'Скрыто'
                            : 'Новое'}
                      </Badge>
                    </div>

                    <p className="text-sm text-gray-600">{rec.description}</p>
                  </div>

                  {rec.status === 'new' && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={updatingId === rec.id}
                        onClick={() => handleRecAction(rec.id, 'done')}
                        title="Отметить выполненной"
                        className="p-1 text-green-600 hover:bg-green-50 hover:text-green-700"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={updatingId === rec.id}
                        onClick={() => handleRecAction(rec.id, 'dismissed')}
                        title="Скрыть рекомендацию"
                        className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
